import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';

import { AdsFeed } from '@/ads/AdsFeed';
import { CalculatorsSection } from '@/calculators/CalculatorsSection';
import { ListingCard } from './ListingCard';
import { categoryLabel, useCategoriesQuery, useListingsQuery, type ListingFeed } from './hooks';
import styles from './FeedPage.module.css';

// Экран лент: 3 вкладки (Спрос / Предложения / Объявления), строка поиска,
// фильтр по категориям, список карточек, догрузка «Показать ещё».
//
// Решения:
//   * `q` и `category` живут в query-string — лента шарится ссылкой и состояние
//     переживает переход в карточку и обратно;
//   * пагинация — кнопка (а не бесконечная прокрутка): keyset-курсор одинаково
//     ложится на оба варианта, но кнопка оставляет футер (оферта/контакты)
//     достижимым и детерминирована в тестах;
//   * фильтра по городу нет: справочника городов в контракте нет, а выдумывать
//     UI-контрол сверх прототипа мы не имеем права (параметр city в хуке есть).

export const FEEDS = ['demand', 'offer', 'notice'] as const;
export const DEFAULT_FEED: ListingFeed = 'offer';

function isFeed(value: string | undefined): value is ListingFeed {
  return (FEEDS as readonly string[]).includes(value ?? '');
}

const SEARCH_DEBOUNCE_MS = 300;

export function FeedPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { feed } = useParams<{ feed: string }>();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const urlQuery = searchParams.get('q') ?? '';
  const category = searchParams.get('category') ?? '';
  const [searchInput, setSearchInput] = useState(urlQuery);

  // Debounce ввода: пока пользователь печатает, запрос не улетает.
  useEffect(() => {
    if (searchInput === urlQuery) return;
    const timer = setTimeout(() => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (searchInput) next.set('q', searchInput);
          else next.delete('q');
          return next;
        },
        { replace: true },
      );
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, urlQuery, setSearchParams]);

  const categoriesQuery = useCategoriesQuery();
  const categories = categoriesQuery.data?.items;

  const listings = useListingsQuery({
    feed: isFeed(feed) ? feed : DEFAULT_FEED,
    ...(urlQuery ? { q: urlQuery } : {}),
    ...(category ? { category } : {}),
  });

  if (!isFeed(feed)) {
    return <Navigate to={`/feed/${DEFAULT_FEED}`} replace />;
  }

  const items = listings.data?.pages.flatMap((page) => page.items) ?? [];
  const isFiltered = Boolean(urlQuery || category);

  function selectCategory(next: string): void {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next) params.set('category', next);
        else params.delete('category');
        return params;
      },
      { replace: true },
    );
  }

  return (
    <div>
      <nav className={styles.tabs} aria-label={t('app.name')}>
        {FEEDS.map((key) => (
          <Link
            key={key}
            to={{ pathname: `/feed/${key}`, search: location.search }}
            className={`${styles.tab} ${key === feed ? styles.tabActive : ''}`}
            aria-current={key === feed ? 'page' : undefined}
          >
            {t(`tabs.${key}`)}
          </Link>
        ))}
      </nav>

      <div className={styles.searchRow}>
        <div className={styles.searchBox}>
          <Search size={15} aria-hidden="true" />
          <input
            className={styles.searchInput}
            type="search"
            aria-label={t('search.label')}
            placeholder={t('search.placeholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
      </div>

      <div className={styles.categories} role="group" aria-label={t('categories.label')}>
        <button
          type="button"
          className={`${styles.categoryPill} ${category === '' ? styles.categoryPillActive : ''}`}
          aria-pressed={category === ''}
          onClick={() => selectCategory('')}
        >
          {t('categories.all')}
        </button>
        {(categories ?? []).map((item) => (
          <button
            key={item.id}
            type="button"
            className={`${styles.categoryPill} ${
              category === item.id ? styles.categoryPillActive : ''
            }`}
            aria-pressed={category === item.id}
            onClick={() => selectCategory(item.id)}
          >
            {categoryLabel(categories, item.id, i18n.language)}
          </button>
        ))}
      </div>

      {listings.isPending && <div className={styles.state}>{t('feed.loading')}</div>}

      {listings.isError && (
        <div className={`${styles.state} ${styles.stateError}`}>
          <div>{t('feed.error')}</div>
          <div className={styles.stateAction}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => void listings.refetch()}
            >
              {t('feed.retry')}
            </button>
          </div>
        </div>
      )}

      {listings.isSuccess && items.length === 0 && (
        <div className={styles.state}>{isFiltered ? t('feed.emptyFiltered') : t('feed.empty')}</div>
      )}

      {items.length > 0 && (
        <div className={styles.list}>
          {items.map((listing) => (
            <ListingCard
              key={listing.id}
              listing={listing}
              categoryName={categoryLabel(categories, listing.category_id, i18n.language)}
              language={i18n.language}
            />
          ))}
        </div>
      )}

      {listings.hasNextPage && (
        <div className={styles.moreRow}>
          <button
            type="button"
            className={styles.secondaryButton}
            disabled={listings.isFetchingNextPage}
            onClick={() => void listings.fetchNextPage()}
          >
            {listings.isFetchingNextPage ? t('feed.loadingMore') : t('feed.loadMore')}
          </button>
        </div>
      )}

      <CalculatorsSection />
      <AdsFeed />
    </div>
  );
}
